/**
 * Typed failures of the RoadSense fetches, so the screens can show "not found",
 * "still working", "no access" or "retry" copy. Kept free of React and `@src`
 * imports so jest can test it directly.
 */
import { ForbiddenError } from "../../auth/services/auth-errors";

export class RoadSenseReportNotFoundError extends Error {
  readonly kind = "not_found" as const;
  constructor(message = "Report not found") {
    super(message);
    this.name = "RoadSenseReportNotFoundError";
  }
}

export class RoadSenseReportNotReadyError extends Error {
  readonly kind = "not_ready" as const;
  readonly status: "generating" | "failed";
  constructor(status: "generating" | "failed", message = "Report not ready") {
    super(message);
    this.name = "RoadSenseReportNotReadyError";
    this.status = status;
  }
}

/** What the RoadSense screens show when a camera-scoped route answers 403 ({@link isNoReportAccess}). */
export const NO_REPORT_ACCESS_MESSAGE = {
  title: "No access",
  body: "You don't have access to RoadSense reports for this account. Ask your account administrator for access to its cameras.",
} as const;

/**
 * Whether a failed RoadSense request means the caller has no access in the
 * tenant. The camera-scoped routes answer 403 only when the caller holds no
 * policy there (a report or site outside their scope is a 404), and
 * `authenticatedFetch` throws that 403 as a `ForbiddenError` rather than
 * returning it. The error is left as is, so the app's error reporting still
 * classifies it as an expected 403.
 */
export function isNoReportAccess(error: unknown): boolean {
  return error instanceof ForbiddenError;
}

/**
 * 403 maps to not-found: the server answers 403 for another tenant's report so
 * ids can't be probed, but to this tenant it simply doesn't exist. 409 means
 * the report has never completed a generation.
 */
export async function liveResponseError(response: Response): Promise<Error | undefined> {
  if (response.ok) return undefined;
  if (response.status === 404 || response.status === 403) {
    return new RoadSenseReportNotFoundError();
  }
  if (response.status === 409) {
    return new RoadSenseReportNotReadyError(await readNotReadyStatus(response));
  }
  const text = await response.text().catch(() => "");
  return new Error(text || `Live payload request failed: ${String(response.status)}`);
}

async function readNotReadyStatus(response: Response): Promise<"generating" | "failed"> {
  try {
    const body: unknown = await response.json();
    const status =
      typeof body === "object" && body !== null && "details" in body ? readStatus(body.details) : undefined;
    if (status !== undefined) return status;
  } catch {
    // Body wasn't JSON. Fall through to the safer "still working" copy.
  }
  return "generating";
}

function readStatus(details: unknown): "generating" | "failed" | undefined {
  if (typeof details !== "object" || details === null || !("status" in details)) return undefined;
  return details.status === "generating" || details.status === "failed" ? details.status : undefined;
}

/**
 * Whether a failed fetch means the server refused this report to this user, so
 * a report already on screen must go. A 404 always is. On the scoped route a
 * 403 (`ForbiddenError`) is too; on the current route it isn't, because
 * `authenticatedFetch` also throws it when a token refresh fails. "Not ready"
 * (409) and transient failures (network, timeout, 5xx) never are.
 */
export function isReportRefusal(error: unknown, route: { cameraScoped: boolean }): boolean {
  if (error instanceof RoadSenseReportNotFoundError) return true;
  return route.cameraScoped && error instanceof ForbiddenError;
}

/**
 * The error the screen shows. A failed background poll keeps the last good
 * report on screen rather than showing the error, unless the server refused
 * the report ({@link isReportRefusal}), which always shows.
 */
export function visibleReportError(state: {
  hasReport: boolean;
  error: Error | null;
  cameraScoped: boolean;
}): Error | null {
  if (isReportRefusal(state.error, state)) return state.error;
  return state.hasReport ? null : state.error;
}
